import { EPropertyType } from '@modules/project-creator/shared/enums/property-type.enum';
import { EParkingType } from '@modules/project-creator/shared/enums/parking-type.enum';
import { EMaterialsType } from '@modules/project-creator/shared/enums/materials-type.enum';
import { ETimeType } from '@modules/project-creator/shared/enums/time-type.enum';
import { ContactType } from '@modules/contacts/shared/enums/contact-type.enum';
import { EProjectScope, IConfiguratorCounterItem, IConfiguratorOption } from '../models/configurator.model';
import { ERoom } from './configurator-rooms.enum';

const O = 'ProjectConfiguratorV2.Options';

/** Chip options with translation keys `ProjectConfiguratorV2.Options.<group>.<value>`. */
const chipOptions = (group: string, values: string[], muted: string[] = []): IConfiguratorOption[] =>
  values.map((value) => ({ value, label: `${O}.${group}.${value}`, muted: muted.includes(value) }));

/** Line icons of the configurator (from the prototype), drawn with currentColor. */
export const configuratorIcon = (name: string): string => `./assets/img/configurator/${name}.svg`;

const PROJECT_SCOPE_ICONS: Record<EProjectScope, string> = {
  [EProjectScope.FLAT]: configuratorIcon('apartment'),
  [EProjectScope.HOUSE]: configuratorIcon('house'),
  [EProjectScope.BATHROOM]: configuratorIcon('bath'),
  [EProjectScope.KITCHEN]: configuratorIcon('kitchen'),
  [EProjectScope.SINGLE_WORKS]: configuratorIcon('tools'),
  [EProjectScope.COMMERCIAL]: configuratorIcon('shop'),
  [EProjectScope.EXTERIOR]: configuratorIcon('roof'),
  [EProjectScope.MULTI_FAMILY]: configuratorIcon('mfh')
};

export const PROJECT_SCOPE_OPTIONS: IConfiguratorOption[] = Object.values(EProjectScope).map((value) => ({
  value,
  label: `${O}.projectScope.${value}.label`,
  description: `${O}.projectScope.${value}.description`,
  svgIcon: PROJECT_SCOPE_ICONS[value]
}));

export const TRADE_OPTIONS = chipOptions('trades', [
  'electrical',
  'plumbing',
  'heating',
  'windows',
  'doors',
  'flooring',
  'tiling',
  'painting',
  'drywall',
  'walls'
]);

export const EXTERIOR_WORK_NONE = 'none';

export const EXTERIOR_WORK_OPTIONS = chipOptions(
  'exteriorWorks',
  ['roof', 'dormer', 'insulation', 'facade', 'terrace', 'garden', EXTERIOR_WORK_NONE],
  [EXTERIOR_WORK_NONE]
);

// Values of EProjectGoal from the old project creator.
export const PROJECT_GOAL_OPTIONS = chipOptions('projectGoal', ['self', 'rent', 'sale', 'commercial']);

// Step 2 "Immobilie"

export const UNKNOWN_VALUE = 'unknown';

/** Yes / no / don't know (D3 in the plan: also used for fields that are boolean in the old API). */
export const YES_NO_UNKNOWN_OPTIONS = chipOptions('yesNo', ['yes', 'no', UNKNOWN_VALUE], [UNKNOWN_VALUE]);

/** House types offered when the whole house is renovated. */
export const HOUSE_PROPERTY_TYPES: string[] = [
  EPropertyType.singleFamily,
  EPropertyType.twinHouse,
  EPropertyType.rowHouse,
  EPropertyType.villa
];

// Values of EPropertyType from the old project creator.
export const PROPERTY_TYPE_OPTIONS: IConfiguratorOption[] = [
  { value: EPropertyType.flat, svgIcon: configuratorIcon('apartment') },
  { value: EPropertyType.singleFamily, svgIcon: configuratorIcon('house') },
  { value: EPropertyType.twinHouse, svgIcon: configuratorIcon('dhh') },
  { value: EPropertyType.rowHouse, svgIcon: configuratorIcon('rh') },
  { value: EPropertyType.villa, svgIcon: configuratorIcon('villa') },
  { value: EPropertyType.townhouse, svgIcon: configuratorIcon('mfh') },
  { value: EPropertyType.commercial, svgIcon: configuratorIcon('shop') }
].map((option) => ({ ...option, label: `${O}.propertyType.${option.value}` }));

export const COMMON_AREA_NONE = 'none';

export const COMMON_AREA_OPTIONS = chipOptions(
  'commonAreas',
  ['staircase', 'basement', 'facade', 'roof', 'technical', 'outdoor', COMMON_AREA_NONE],
  [COMMON_AREA_NONE]
);

export const UNITS_OCCUPIED_OPTIONS = chipOptions('unitsOccupied', ['all', 'partly', 'vacant']);

export const COMMERCIAL_USE_OPTIONS = chipOptions('commercialUse', [
  'office',
  'practice',
  'shop',
  'restaurant',
  'storage'
]);

export const PROPERTY_AGE_OPTIONS = chipOptions(
  'propertyAge',
  ['before-1950', '1950-1969', '1970-1993', '1994-2009', 'from-2010', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const ROOM_HEIGHT_OPTIONS = chipOptions(
  'roomHeight',
  ['normal', 'higher', 'period', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

// Values of EParkingType from the old project creator.
export const PARKING_SITUATION_OPTIONS = chipOptions(
  'parkingSituation',
  [EParkingType.good, EParkingType.bad, EParkingType.private, EParkingType.unknown],
  [EParkingType.unknown]
);

export const OWNERS_ASSOCIATION = 'owners-association';

export const OWNERSHIP_TYPE_OPTIONS = chipOptions('ownershipType', [
  'owner',
  OWNERS_ASSOCIATION,
  'purchase-pending',
  'tenant'
]);

export const EMPTY_AT_START_OPTIONS = chipOptions('emptyAtStart', ['yes', 'no']);

// Step 3 "Umfang"

export const NONE_VALUE = 'none';

export const ROOM_ITEMS: IConfiguratorCounterItem[] = Object.values(ERoom).map((key) => ({
  key,
  label: `${O}.rooms.${key}`
}));

export const SHOWER_TYPES_WITH_FITTINGS = ['level-access', 'shower-tray'];
export const SHOWER_OPTIONS = chipOptions(
  'shower',
  [...SHOWER_TYPES_WITH_FITTINGS, NONE_VALUE],
  [NONE_VALUE]
);

export const BATHTUB_TYPES_WITH_FITTINGS = ['freestanding', 'built-in'];
export const BATHTUB_OPTIONS = chipOptions(
  'bathtub',
  [...BATHTUB_TYPES_WITH_FITTINGS, NONE_VALUE],
  [NONE_VALUE]
);

export const FITTINGS_OPTIONS = chipOptions(
  'fittings',
  ['surface', 'concealed', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);
export const BATHTUB_FITTINGS_OPTIONS = chipOptions(
  'fittings',
  ['surface', 'concealed', 'floor-standing', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const WASHBASIN_OPTIONS = chipOptions('washbasin', ['single', 'double']);

export const TOILET_OPTIONS = chipOptions(
  'toilet',
  ['wall-hung', 'floor-standing', 'shower-toilet', NONE_VALUE],
  [NONE_VALUE]
);
export const GUEST_WC_TOILET_OPTIONS = chipOptions('toilet', [
  'wall-hung',
  'floor-standing',
  'shower-toilet'
]);

export const BATHROOM_EXTRA_OPTIONS = chipOptions('bathroomExtras', [
  'towel-radiator',
  'window',
  'ventilation',
  'mirror-cabinet',
  'niche'
]);

export const WALL_TILES_OPTIONS = chipOptions(
  'wallTiles',
  ['full-height', 'half-height', 'two-walls', 'seamless', NONE_VALUE, UNKNOWN_VALUE],
  [NONE_VALUE, UNKNOWN_VALUE]
);
export const GUEST_WC_WALL_OPTIONS = chipOptions(
  'wallTiles',
  ['full-height', 'half-height', 'seamless', NONE_VALUE, UNKNOWN_VALUE],
  [NONE_VALUE, UNKNOWN_VALUE]
);

export const GUEST_WC_BASIN_OPTIONS = chipOptions(
  'guestWcBasin',
  ['hand-basin', 'vanity', NONE_VALUE],
  [NONE_VALUE]
);

export const KITCHEN_SUPPLIER_OPTIONS = chipOptions('kitchenSupplier', ['studio', 'stays']);

export const ELECTRICITY_TYPES_WITH_WISHES = ['complete', 'partial'];
export const ELECTRICITY_TYPE_OPTIONS = chipOptions(
  'electricityType',
  [...ELECTRICITY_TYPES_WITH_WISHES, 'stays', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const ELECTRICAL_WISH_OPTIONS = chipOptions('electricalWishes', [
  'sockets',
  'spots',
  'network',
  'smart-home',
  'electric-shutters',
  'wallbox',
  'pv'
]);

// gas, oil, heat-pump, town-heating and electric are values of HeatingList from the old project creator.
export const HEATING_TYPE_OPTIONS = chipOptions(
  'heatingType',
  ['gas', 'oil', 'heat-pump', 'town-heating', 'electric', 'pellets', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const HEATING_LOCATION_OPTIONS = chipOptions(
  'heatingLocation',
  ['central', 'apartment', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

// heater and floor-heating are values of EnergySource from the old project creator.
export const ENERGY_SOURCE_OPTIONS = chipOptions(
  'energySource',
  ['heater', 'floor-heating', 'mixed', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const HEATING_PLAN_OPTIONS = chipOptions(
  'heatingPlan',
  ['stays', 'new-radiators', 'add-floor-heating', 'new-system', 'advise'],
  ['advise']
);

export const WALLPAPER_OPTIONS = chipOptions(
  'wallpaper',
  ['yes', 'partly', 'no', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const CEILING_OPTIONS = chipOptions('ceiling', ['suspended', 'spots', 'keep-stucco', 'paint-only']);

export const FLOOR_CURRENT_OPTIONS = chipOptions(
  'floorCurrent',
  ['carpet', 'laminate', 'parquet', 'tiles', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const FLOOR_WANTED_OPTIONS = chipOptions(
  'floorWanted',
  ['parquet', 'vinyl', 'tiles', 'carpet', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const WINDOWS_REPLACED = ['all', 'partly'];
export const WINDOWS_REPLACE_OPTIONS = chipOptions(
  'windowsReplace',
  [...WINDOWS_REPLACED, 'no', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const WINDOW_ELEMENT_ITEMS: IConfiguratorCounterItem[] = [
  'window',
  'balcony-door',
  'lift-slide-door',
  'roof-window'
].map((key) => ({ key, label: `${O}.windowElements.${key}` }));

export const INTERIOR_DOOR_ITEMS: IConfiguratorCounterItem[] = [
  'standard',
  'loft',
  'flush',
  UNKNOWN_VALUE
].map((key) => ({ key, label: `${O}.interiorDoors.${key}` }));

export const ROOF_AGE_OPTIONS = chipOptions(
  'roofAge',
  ['under-20', '20-40', 'over-40', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

export const PLANNING_SERVICE_OPTIONS = chipOptions(
  'planningServices',
  ['floor-plan', 'bathroom', 'visualisation', 'electrical', NONE_VALUE],
  [NONE_VALUE]
);

// Step 6 "Budget & Zeitrahmen"

export const BUDGET_RANGE_OPTIONS = chipOptions(
  'budgetRange',
  [
    '20k-50k',
    '50k-100k',
    '100k-200k',
    '200k-300k',
    '300k-500k',
    '500k-800k',
    '800k-1m',
    'over-1m',
    UNKNOWN_VALUE
  ],
  [UNKNOWN_VALUE]
);

const PRIORITY_ICONS: Record<string, string> = {
  quality: configuratorIcon('sparkle'),
  price: configuratorIcon('euro'),
  speed: configuratorIcon('zap')
};

export const PRIORITY_OPTIONS: IConfiguratorOption[] = Object.entries(PRIORITY_ICONS).map(
  ([value, svgIcon]) => ({
    value,
    label: `${O}.priorities.${value}.label`,
    description: `${O}.priorities.${value}.description`,
    note: `${O}.priorities.${value}.note`,
    svgIcon
  })
);

export const DEADLINE_FIXED = 'fixed';

export const DEADLINE_TYPE_OPTIONS = chipOptions('deadlineType', [DEADLINE_FIXED, 'flexible']);

export const DEADLINE_REASON_OPTIONS = chipOptions('deadlineReason', [
  'move-in',
  'lease-end',
  'sale-or-rent'
]);

// Step 4 "Ausstattung"

const MATERIAL_LINE_COVERS: Record<EMaterialsType, string> = {
  [EMaterialsType.basic]: 'linear-gradient(135deg, #eef1f6, #d9dfea)',
  [EMaterialsType.comfort]: 'linear-gradient(135deg, #e9e3d8, #c9b9a1)',
  [EMaterialsType.premium]: 'linear-gradient(135deg, #3b3a40, #16161b)'
};

const MATERIAL_LINE_FEATURE_COUNT: Record<EMaterialsType, number> = {
  [EMaterialsType.basic]: 4,
  [EMaterialsType.comfort]: 4,
  [EMaterialsType.premium]: 5
};

/** Equipment lines; values are the `EMaterialsType` of the old creator. */
export const MATERIAL_LINE_OPTIONS: IConfiguratorOption[] = Object.values(EMaterialsType).map((value) => ({
  value,
  label: `${O}.materials.${value}.label`,
  description: `${O}.materials.${value}.description`,
  tag: `${O}.materials.${value}.price`,
  cover: MATERIAL_LINE_COVERS[value],
  features: Array.from(
    { length: MATERIAL_LINE_FEATURE_COUNT[value] },
    (_, index) => `${O}.materials.${value}.features.${index}`
  )
}));

export const MATERIAL_SELECTION_OPTIONS = chipOptions(
  'materialSelection',
  ['sampling', 'partly-own', UNKNOWN_VALUE],
  [UNKNOWN_VALUE]
);

// Step 7 "Kontakt"

export const CUSTOMER_TYPE_OPTIONS: IConfiguratorOption[] = [
  { value: ContactType.PERSONAL, label: `${O}.customerType.personal`, svgIcon: configuratorIcon('person') },
  { value: ContactType.COMPANY, label: `${O}.customerType.company`, svgIcon: configuratorIcon('mfh') }
];

export const COMPANY_ROLE_OPTIONS = chipOptions('companyRole', [
  'developer',
  'property-management',
  'investor',
  'commercial-tenant'
]);

export const SALUTATION_NONE = 'none';
export const SALUTATION_OPTIONS = chipOptions('salutation', ['ms', 'mr', SALUTATION_NONE], [SALUTATION_NONE]);

export const INVOICE_RECIPIENT_CUSTOMER = 'customer';
export const INVOICE_RECIPIENT_OTHER = 'other';
/** The description of "to this address" is replaced by the typed address, see the contact step. */
export const INVOICE_RECIPIENT_OPTIONS: IConfiguratorOption[] = [
  {
    value: INVOICE_RECIPIENT_CUSTOMER,
    label: `${O}.invoiceRecipient.customer.label`,
    description: `${O}.invoiceRecipient.customer.description`,
    svgIcon: configuratorIcon('pin')
  },
  {
    value: INVOICE_RECIPIENT_OTHER,
    label: `${O}.invoiceRecipient.other.label`,
    description: `${O}.invoiceRecipient.other.description`,
    svgIcon: configuratorIcon('file')
  }
];

export const CONTACT_CHANNEL_OPTIONS = chipOptions('contactChannels', ['phone', 'email', 'whatsapp']);

/** Values of the old creator (`ETimeType`); several can be chosen now. */
export const CONTACT_TIME_OPTIONS = chipOptions('contactTime', Object.values(ETimeType));

export const LEAD_SOURCE_REFERRAL = 'referral';
export const LEAD_SOURCE_OTHER = 'other';
export const LEAD_SOURCE_OPTIONS = chipOptions('leadSource', [
  'instagram',
  'google',
  LEAD_SOURCE_REFERRAL,
  'existing-customer',
  LEAD_SOURCE_OTHER
]);
